import styled from "@emotion/styled";
import { noop } from "lodash";
import { FC } from "react";
import { Field, FieldRenderProps, FormRenderProps, useForm } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { useSelector } from "react-redux";

import { Add, Check } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  FormField,
  IPickerListItem,
  Icon,
  Input,
  Picker,
  SpacingToken,
  Text,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { PaymentFailedAlert, PaymentFailedLocation } from "components/payment-failures-limits/PaymentFailedAlert";
import { SeatsExceededMessage } from "components/payment-failures-limits/SeatsExceededMessage";
import Role, { readableRoles } from "constants/role";
import { usePartialInvite } from "hooks/use-partial-invite";
import { PaymentFailedError } from "hooks/use-payment-failure";
import { useUpgradeSubscription } from "hooks/use-subscription-upgrade";
import { selectIsAdmin, selectLoggedInAgentLogin } from "store/entities/agents";
import { selectIsShopifyTrial } from "store/entities/integrations";
import { getPartialInvite } from "store/views/welcome-tutorial";
import { withVar } from "themes/helpers";
import { ISimpleAgentFormRow, getNonViewersToAdd } from "utils/agent";

interface IProps {
  props: FormRenderProps<{
    agents: ISimpleAgentFormRow[];
  }>;
  failed: boolean;
  paymentError: PaymentFailedError;
}

export const InviteForm: FC<IProps> = ({ props: { handleSubmit, values }, failed, paymentError }) => {
  const form = useForm();

  const { successfulAgents, failedAgents, exceededBy } = useSelector(getPartialInvite);
  const isShopifyTrial = useSelector(selectIsShopifyTrial);

  const skipSeatsExceededNotification = isShopifyTrial;

  usePartialInvite(payload => {
    if (payload.failedAgents.length) {
      form.reset({ agents: payload.failedAgents });
      form.change("agents", payload.failedAgents);
    }
  });

  if (exceededBy) {
    return (
      <>
        <Block paddingV={withVar(SpacingToken.Spacing2)}>
          <PaymentFailedAlert location={PaymentFailedLocation.Onboarding} error={paymentError} autoScroll />
        </Block>
        {successfulAgents?.map(agent => (
          <Flex key={agent.email} justifyContent="space-between" data-testid={`partial-successful-${agent.email}`}>
            <Text>
              {agent.email} ({readableRoles[agent.permission].name})
            </Text>
            <Flex gap={withVar(SpacingToken.Spacing1)}>
              <Icon source={Check} customColor={withVar(DesignToken.ActionPositiveDefault)} size="small" />{" "}
              <Text bold>Invited</Text>
            </Flex>
          </Flex>
        ))}
        <form onSubmit={handleSubmit}>
          {failedAgents?.map((agent, index) => (
            <AgentRow
              key={agent.email}
              email={agent.email}
              role={agent.permission}
              data-testid={`partial-failed-${agent.email}`}
              idx={index}
            />
          ))}
        </form>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Block paddingV={withVar(SpacingToken.Spacing2)}>
        {failed && <PaymentFailedAlert location={PaymentFailedLocation.Onboarding} error={paymentError} autoScroll />}
      </Block>
      <FieldArray name="agents">
        {({ fields }) => (
          <>
            <AgentRow inert header />
            {fields.map((name: string, index: number) => (
              <AgentRow key={name} name={name} idx={index} />
            ))}
            <Button kind="link" onClick={() => fields.push({ email: null, role: null })} icon={<Add />}>
              Invite more
            </Button>
          </>
        )}
      </FieldArray>
      {!skipSeatsExceededNotification && <SeatsExceeded nonViewersToAdd={getNonViewersToAdd(values.agents)} />}
    </form>
  );
};

const AgentRow: FC<{
  name?: string;
  idx?: number;
  inert?: boolean;
  email?: string;
  role?: Role;
  header?: boolean;
  "data-testid"?: string;
}> = ({ idx, inert = false, email, role, header = false, "data-testid": dataTestId }) => {
  if (inert) {
    return (
      <Flex gap={withVar(SpacingToken.Spacing2)} data-testid={dataTestId}>
        <EmailInput email={email} header={header} />
        <RolePicker role={role} header={header} />
      </Flex>
    );
  }

  return (
    <Field name={`agents.${idx}`}>
      {({ input }) => (
        <Flex gap={withVar(SpacingToken.Spacing2)} data-testid={dataTestId}>
          <Field name={`${input.name}.email`}>{props => <EmailInput {...props} />}</Field>
          <Field name={`${input.name}.permission`}>{props => <RolePicker {...props} />}</Field>
        </Flex>
      )}
    </Field>
  );
};

const EmailInput: FC<Partial<FieldRenderProps<string>>> = ({ input, meta, email, header }) => {
  const currentUserEmail = useSelector(selectLoggedInAgentLogin);

  if (!input) {
    return (
      <EmailFormField labelText={header ? "Teammate's email address" : ""}>
        <Input placeholder="email" value={email ?? `${currentUserEmail} (You)`} disabled inputSize="large" />
      </EmailFormField>
    );
  }

  const error = meta && meta.touched && meta.error;

  return (
    <EmailFormField error={error}>
      <Input
        placeholder="Email address"
        {...input}
        data-1p-ignore
        autoComplete="off"
        data-testid={input.name}
        inputSize="large"
      />
    </EmailFormField>
  );
};

const EmailFormField = styled(FormField)`
  margin-bottom: ${withVar(SpacingToken.Spacing4)};
  flex-basis: 50%;
`;

const createRoleOption = (role: Role): IPickerListItem => ({
  key: role,
  name: readableRoles[role].name,
  customElement: {
    selectedItemBody: <Text>{readableRoles[role].name}</Text>,
    listItemBody: <RoleListItemBody role={role} />,
  },
});

const RoleListItemBody: FC<{ role: Role }> = ({ role }) => {
  return (
    <>
      <Text size="md" bold customColor={withVar(DesignToken.ContentBasicPrimary)}>
        {readableRoles[role].name}
      </Text>
      <Text size="xs" customColor={withVar(DesignToken.ContentBasicSecondary)}>
        {readableRoles[role].description}
      </Text>
    </>
  );
};

const getRoleOptions = (isAdmin: boolean): IPickerListItem[] => {
  return (isAdmin ? [Role.Owner, Role.Normal, Role.Viewer] : [Role.Normal, Role.Viewer]).map(createRoleOption);
};

const RolePicker: FC<Partial<FieldRenderProps<Role>>> = ({ input, meta, role, header }) => {
  const isAdmin = useSelector(selectIsAdmin);
  if (!input) {
    const selectedOption = [
      role ? createRoleOption(role) : isAdmin ? createRoleOption(Role.Owner) : createRoleOption(Role.Normal),
    ];
    return (
      <RoleFormField labelText={header ? "Role" : ""}>
        <Picker
          placeholder="Select"
          onSelect={noop}
          options={selectedOption}
          selected={selectedOption}
          hideClearButton
          disabled
          size="large"
        />
      </RoleFormField>
    );
  }

  const options = getRoleOptions(isAdmin);
  const foundOption = options.find(option => option.key === input.value);
  const selectedOption = foundOption ? [foundOption] : null;

  const onSelect = (role: IPickerListItem[] | null): void => {
    if (!role) return;
    input.onChange(role[0].key);
  };

  return (
    <RoleFormField error={meta?.submitFailed && meta?.error}>
      <Picker
        placeholder="Select"
        onSelect={onSelect}
        options={options}
        selected={selectedOption}
        hideClearButton
        searchDisabled
        onOpen={() => input.onFocus()}
        onClose={() => input.onBlur()}
        data-testid={input.name}
        size="large"
      />
    </RoleFormField>
  );
};

const RoleFormField = styled(FormField)`
  margin-bottom: ${withVar(SpacingToken.Spacing4)};
  flex-basis: 50%;
`;

export const SeatsExceeded: FC<{ nonViewersToAdd: number }> = ({ nonViewersToAdd }) => {
  const { willUpgrade, exceededBy } = useUpgradeSubscription(nonViewersToAdd);

  return (
    willUpgrade && (
      <Block marginTop={withVar(SpacingToken.Spacing2)}>
        <SeatsExceededMessage limitExceededBy={exceededBy} />
      </Block>
    )
  );
};
